<!--suppress ALL -->
<template>
    <div class="content-top">
        <Head title="New Challenge"></Head>

        <heading class="mx-2 lg:mx-0">
            <template #heading>
                <span>New Challenge</span>
            </template>
        </heading>

        <div class="bg-white dark:bg-slate-800 lg:max-w-4xl lg:rounded-lg lg:shadow">
            <form @submit.prevent="showTermsModal">
                <div class="flex flex-col divide-gray-200 dark:divide-slate-700 lg:divide-y">
                    <div class="px-4 py-4 w-full lg:w-1/2">
                        <participant-option v-model="form.type" />
                        <alert-error v-if="form.errors.type">
                            {{ form.errors.type }}
                        </alert-error>
                    </div>
                    <!--Media & Description-->
                    <div class="flex flex-col divide-white dark:divide-slate-700 lg:flex-row lg:divide-x">
                        <!--Description-->
                        <div class="flex flex-col justify-between w-full lg:w-1/2">
                            <div class="flex flex-col h-36 lg:h-full">
                                <textarea
                                    ref="description"
                                    v-model="form.text"
                                    :class="[form.errors.text ? 'ring-red-500 border-red-500 placeholder-red-500' : 'border-0', mediaItemCount > 3 ? 'lg:h-88' : 'lg:h-44']"
                                    class="flex-1 px-4 py-2 w-full h-36 text-sm dark:text-slate-100 border-0 rounded-0 focus:outline-none resize-none focus:ring-2 focus:ring-transparent lg:p-4 lg:text-base dark:bg-slate-600 dark:placeholder-slate-200"
                                    placeholder="Write the challenge details..."
                                    :disabled="form.processing"
                                >
                                </textarea>
                                <alert-error v-if="form.errors.text">
                                    {{ form.errors.text }}
                                </alert-error>
                            </div>
                        </div>

                        <!-- HasMedia -->
                        <div class="px-2 py-4 w-full lg:p-4 lg:w-1/2">
                            <media-library-attachment ref="mediaUploadRef" class="mb-2" :max-items="maxMediaItems" :sortable="false" :validation-errors="form.errors" :initial-value="form.media" multiple :validation-rules="validationRules" route-prefix="media-library" @change="onChange" name="media">
                                <template slot="properties" slot-scope="{ object }">
                                    <div class="media-library-property"></div>
                                </template>
                            </media-library-attachment>
                            <div class="grid gap-2 grid-cols-3">
                                <button
                                    v-show="mediaItemCount < maxMediaItems"
                                    type="button"
                                    @click="$root.$emit('addMediaToPost')"
                                    class="inline-flex col-span-3 items-center justify-center px-2 py-1.5 w-full text-gray-600 dark:text-slate-200 hover:text-brand-500 dark:hover:text-slate-400 text-xs font-medium bg-gray-100 dark:bg-slate-900 hover:bg-gray-200 dark:hover:bg-slate-900/50 border border-transparent rounded-md focus:outline-none uppercase space-x-1 transition duration-150 ease-in-out focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 lg:col-span-1 lg:ml-0"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span>Add Media</span>
                                </button>
                            </div>
                            <alert-error v-if="form.errors.media">{{ form.errors.media }}</alert-error>
                        </div>
                    </div>

                    <!--Anonymous-->
                    <div>
                        <div class="pb-4 px-4 w-full lg:pt-4 lg:w-1/2">
                            <toggle-left-label v-model="form.anonymous" aria="anonymous">
                                <template #label>I want to be anonymous</template>
                                <template #description>Your name and photo will be hidden from the challenge.</template>
                            </toggle-left-label>
                            <alert-error v-if="form.errors.anonymous">
                                {{ form.errors.anonymous }}
                            </alert-error>
                        </div>

                        <!-- Submit Button -->
                        <div class="flex flex-col-reverse items-center justify-end px-4 py-4 text-right bg-gray-50 dark:bg-slate-900/25 rounded-b-lg sm:flex-row sm:px-6">
                            <jet-action-message :show="form.recentlySuccessful" class="mt-3 sm:mr-3 sm:mt-0"> Published.</jet-action-message>

                            <jet-action-message :show="form.processing" class="mt-3 sm:mr-3 sm:mt-0"> Uploading...</jet-action-message>

                            <jet-action-message :show="form.hasErrors && form.errors.insufficientFunds" class="mt-3 sm:mr-3 sm:mt-0">
                                <span class="text-red-600 font-medium">
                                    You do not have enough tokens.
                                    <a class="text-indigo-600 hover:text-indigo-700" href="#" @click.prevent="$root.$emit('showBuyTokensModal', 10)">Purchase Tokens Now</a>
                                </span>
                            </jet-action-message>

                            <jet-action-message :show="ignoredTermsModal" class="mt-3 sm:mr-3 sm:mt-0">
                                <span class="text-red-600 font-medium">You must agree to the Community Guidelines before publishing.</span>
                            </jet-action-message>

                            <jet-button type="submit" dusk="submit-button" class="w-full sm:ml-5 lg:w-auto" :disabled="isSubmitDisabled" :processing="form.processing">
                                Next
                                <svg class="-mr-1 ml-3 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </jet-button>
                        </div>

                        <!--<div-->
                        <!--    v-if="$page.props.user.wallet.balance < 10"-->
                        <!--    @click="$root.$emit('showBuyTokensModal', 10)"-->
                        <!--    class="flex flex-col-reverse items-center justify-between -mx-4 pb-8 pt-4 px-4 text-right bg-yellow-50 rounded-b-lg space-y-4 space-y-reverse sm:flex-row sm:px-6 lg:-mx-0 lg:pb-4 lg:space-y-0"-->
                        <!--&gt;-->
                        <!--    <div class="flex items-center">-->
                        <!--        <div class="flex-shrink-0">-->
                        <!--            &lt;!&ndash; Heroicon name: solid/exclamation &ndash;&gt;-->
                        <!--            <svg class="w-12 h-12 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">-->
                        <!--                <path-->
                        <!--                    fill-rule="evenodd"-->
                        <!--                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"-->
                        <!--                    clip-rule="evenodd"-->
                        <!--                />-->
                        <!--            </svg>-->
                        <!--        </div>-->
                        <!--        <div class="ml-3 text-left">-->
                        <!--            <h3 class="text-yellow-800 text-sm font-medium lg:text-base">You Need More Tokens</h3>-->
                        <!--            <div class="text-yellow-700 text-xs lg:text-sm">-->
                        <!--                <p>You must pledge a minimum of 10 tokens towards your new challenge.</p>-->
                        <!--            </div>-->
                        <!--        </div>-->
                        <!--    </div>-->

                        <!--    <div class="flex-shrink-0 w-full lg:w-auto">-->
                        <!--        <jet-button type="button" color="yellow" class="w-full sm:ml-5 lg:w-auto" :processing="form.processing"> Buy Tokens </jet-button>-->
                        <!--    </div>-->
                        <!--</div>-->
                    </div>
                </div>
            </form>
        </div>

        <!-- Warning Modal -->
        <dialog-modal :show="showingTermsModal" @close="agreedTermsModal = false">
            <template #content>
                <div class="-mb-2">
                    <div class="flex items-center justify-end -mr-2 -mt-2">
                        <modal-close @close="ignoreTermsModal" />
                    </div>
                    <h2 class="my-2 text-xl font-medium">Challenge Expiration & Renewal</h2>
                    <p class="my-2 text-gray-800 text-sm">
                        Incomplete challenges will automatically expire 120 days after being published. When a challenge expires, the
                        <span class="has-tooltip inline-block">
                            <abbr class="hover:text-yellow-600 text-yellow-700 cursor-help">Challenger</abbr>
                            <tooltip>Person who creates the challenge</tooltip>
                        </span>
                        will have two options.
                    </p>
                    <ul class="ml-10 text-gray-800 text-sm list-decimal">
                        <li class="px-4 py-1 sm:px-0">
                            Extend the challenge for an additional 120 days.
                            <span class="text-xs">(fees waived)</span>
                        </li>
                        <li class="px-4 py-1 sm:px-0">Archive the challenge and request all pledged tokens be returned to any user who pledged. Pledged tokens will be returned to the user's token wallet.</li>
                    </ul>
                    <hr class="my-4" />
                    <h2 class="mb-2 text-xl font-medium">Community Guidelines</h2>
                    <p class="my-2 text-gray-800 text-sm">Help us to keep Throbo an entertaining, fun, and safe platform for everyone. We reserve the right to remove any content that we believe violates any of our guidelines or policies.</p>
                    <div class="group flex items-start justify-between pt-6">
                        <div class="flex justify-center pr-2 pt-px cursor-pointer sm:w-11 lg:pr-0">
                            <input aria-describedby="community-guidelines-description" v-model="agreedTermsModal" id="terms-checkbox" type="checkbox" class="w-4 h-4 text-indigo-600 border-gray-400 rounded focus:ring-indigo-500" />
                        </div>
                        <div>
                            <p class="mb-2 text-gray-800 text-sm">I agree that this challenge does not encourage, promote, or require any persons whatsoever to:</p>
                            <ul class="ml-10 text-gray-800 text-sm list-disc">
                                <li class="px-4 py-1 sm:px-0">break any laws.</li>
                                <li class="px-4 py-1 sm:px-0">perform any acts that are dangerous or harmful to themselves, other persons, or animals.</li>
                                <li class="px-4 py-1 sm:px-0">bully, harass, opress, subjugate, or terrorize other persons or animals.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex items-center w-full lg:w-auto">
                    <jet-action-message :show="showingCommunityGuidelinesWarning" class="hidden mt-3 sm:mr-3 sm:mt-0 lg:inline-block">
                        <span class="text-red-600 font-medium">Agree to the Community Guidelines before publishing.</span>
                    </jet-action-message>
                    <jet-button dusk="modal-submit-button" class="relative flex-shrink-0 w-full lg:w-auto" :processing="form.processing" @click.native="closeThenSubmitForm" :disabled="!agreedTermsModal">
                        <span @mouseover="showCommunityGuidelinesWarning" @mouseout="hideCommunityGuidelinesWarning" class="absolute inset-0" aria-hidden="true"></span>
                        <span>Next</span>
                        <svg class="-mr-1 ml-3 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </jet-button>
                </div>
            </template>
        </dialog-modal>

        <!--Pledge Modal-->
        <!--<pledge-tokens-modal :show.sync="showPledgeModal" :options="options" />-->
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout"
import JetButton from "@/Jetstream/Button"
import JetInputError from "@/Jetstream/InputError"
import JetInput from "@/Jetstream/Input"
import JetActionMessage from "@/Jetstream/ActionMessage"
import MediaLibraryAttachment from "@/Media-Library/MediaLibraryAttachment"
import Heading from "@/Components/Heading"
import AlertError from "@/Components/Alerts/Error"
import SecondaryButton from "@/Jetstream/SecondaryButton"
import BuyTokensModal from "@/Pages/Wallet/BuyTokensModal"
import Tooltip from "@/Components/Tooltip"
import ModalClose from "@/Components/ModalClose"
import DialogModal from "@/Jetstream/DialogModal"
import PledgeTokensModal from "@/Pages/Wallet/PledgeTokensModal"
import ParticipantOption from "@/Pages/Post/Create/ParticipantOption"
import ToggleRightLabel from "@/Components/ToggleRightLabel"
import ToggleLeftLabel from "@/Components/ToggleLeftLabel"

export default {
    name: "CreatePost",

    layout: AppLayout,

    components: {
        ToggleLeftLabel,
        ToggleRightLabel,
        ParticipantOption,
        PledgeTokensModal,
        DialogModal,
        ModalClose,
        Tooltip,
        BuyTokensModal,
        JetButton,
        AlertError,
        Heading,
        AppLayout,
        MediaLibraryAttachment,
        JetButton,
        JetInputError,
        JetActionMessage,
        SecondaryButton,
        JetInput,
    },

    created() {
        /**
         * This method is used to close a form and then submit it. Throttle clicking the button.
         *
         * @param {HTMLElement} form - The form element that needs to be closed and submitted.
         * @returns {boolean} - Returns true if the form was successfully closed and submitted,
         *                      otherwise returns false.
         */
        this.closeThenSubmitForm = _.throttle(this.closeThenSubmitForm, 2000)
    },

    mounted() {
        this.$nextTick(() => this.$refs.description.focus())

        // Set form.type based on the 'type' query parameter
        const typeQueryParam = this.type // Using the computed property 'type'
        if (typeQueryParam) {
            this.form.type = typeQueryParam
        }
    },

    props: {
        validationRules: {
            type: Object,
            required: true,
        },
        errors: {
            type: Object,
            default: () => ({}),
        },
    },

    watch: {
        type(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.form.type = newVal
            }
        },
        /**
         * Updates the pledge amount in the form object.
         * If the given amount is not within the range of valid pledges,
         * it truncates the current pledge to 7 characters.
         *
         * @param {number} amount - The new pledge amount.
         */
        "form.pledge": function (amount) {
            if (!_.inRange(amount, 1000000)) {
                this.form.pledge = this.form.pledge.toString().substr(0, 7)
            }
        },
        "form.errors.media": function (newVal) {
            if (newVal !== null) {
                this.form.media = []
                this.$emit("resetMediaLibrary")
            }
        },
    },

    computed: {
        /**
         * Returns the count of media items in the form.
         *
         * @returns {number} The count of media items in the form.
         */
        mediaItemCount() {
            return Object.keys(this.form.media).length
        },

        /**
         * Checks if the submit button should be disabled based on the state of the form.
         *
         * @returns {boolean} - Returns true if the submit button should be disabled, otherwise false.
         */
        isSubmitDisabled() {
            const hasMedia = typeof this.form.media === "object" && Object.keys(this.form.media).length > 0
            const hasText = !!this.form.text
            const disabled = !hasMedia || !hasText
            return disabled
        },

        type() {
            return this.$page.props.type
        },
    },

    data() {
        return {
            maxMediaItems: 6,
            showVideoModal: false,
            showingTermsModal: false,
            ignoredTermsModal: false,
            agreedTermsModal: false,
            showingCommunityGuidelinesWarning: false,
            showPledgeTokensModal: false,
            form: this.$inertia.form({
                _method: "POST",
                text: null,
                media: [],
                anonymous: false,
                type: "targeted",
                pledge: 10,
            }),
        }
    },

    methods: {
        showTermsModal() {
            // Before showing the terms modal, validate UUIDs
            if (!this.validateMediaUuids()) {
                // Prevent showing the terms modal if there's an invalid UUID
                alert("Please ensure all uploaded media items are valid.")
                return
            }

            this.showingTermsModal = true
            this.ignoredTermsModal = false
        },
        onChange(media) {
            this.form.media = media
        },

        closeThenSubmitForm() {
            if (this.agreedTermsModal) {
                this.showingTermsModal = false
                this.$nextTick(() => this.storePost())
            }
        },

        storePost() {
            this.form.clearErrors()
            this.form.post(route("posts.store"), {
                preserveScroll: true,
                onFinish: () => (this.agreedTermsModal = false),
                onError: (errors) => {
                    if (errors.media) {
                        this.form.media = []
                        this.clearMediaLibrary()
                    }
                },
            })
        },

        ignoreTermsModal() {
            this.showingTermsModal = false
            this.ignoredTermsModal = true
        },

        showCommunityGuidelinesWarning() {
            if (!this.agreedTermsModal) {
                this.showingCommunityGuidelinesWarning = true
            }
        },

        hideCommunityGuidelinesWarning() {
            if (!this.agreedTermsModal) {
                this.showingCommunityGuidelinesWarning = false
            }
        },
        isValidUuid(uuid) {
            const regexExp = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89ABab][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/
            return regexExp.test(uuid)
        },

        validateMediaUuids() {
            // Check if form.media is an object
            if (typeof this.form.media !== "object" || this.form.media === null) {
                console.error("form.media is not an object or is null", this.form.media)
                // Handle the error appropriately, perhaps set a form error
                this.form.errors.media = "Invalid media data."
                return false
            }

            // Iterate over object properties
            for (const key in this.form.media) {
                if (this.form.media.hasOwnProperty(key)) {
                    const mediaItem = this.form.media[key]
                    // Assuming the correct property is 'uuid' (typo 'uui' in the example might be a mistake)
                    if (!this.isValidUuid(mediaItem.uuid)) {
                        this.form.errors.media = "One or more media items have an invalid UUID."
                        return false
                    }
                }
            }

            // Clear any previous UUID-related errors if all UUIDs are valid
            this.form.errors.media = null
            return true
        },
        clearMediaLibrary() {
            const mediaLibraryComponent = this.$refs.mediaUploadRef
            if (mediaLibraryComponent && mediaLibraryComponent.mediaLibrary) {
                mediaLibraryComponent.mediaLibrary.state.media = []
            }
        },
    },
}
</script>
